<template>
  <div>
    <!-- RSVP summary -->
    <div class="mb-3">
      <div class="section-title">RSVP</div>
      <span v-if="Number(existing.status) === 1" class="badge badge-success">
        <span v-if="rsvpFamilyEnabled">Confirmed • Adults: {{ existing.adult || 0 }}, Child: {{ existing.child || 0 }}</span>
        <span v-else>Confirmed</span>
      </span>
      <span v-else-if="Number(existing.status) === 2" class="badge badge-danger">
        <span v-if="rsvpFamilyEnabled">Not Attending • Adults: 0, Child: 0</span>
        <span v-else>Not Attending</span>
      </span>
      <span v-else-if="Number(existing.status) === 3" class="badge badge-primary">
        <span v-if="rsvpFamilyEnabled">Maybe • Adults: {{ existing.adult || 0 }}, Child: {{ existing.child || 0 }}</span>
        <span v-else>Maybe</span>
      </span>
      <span v-else class="badge badge-secondary">No response</span>

      <div v-if="activeContactLocal" class="text-muted small mt-2">
        You’re viewing RSVP linked to <strong>{{ activeContactLocal }}</strong>.
      </div>
    </div>

    <!-- Actions -->
    <div class="mb-4 rsvp-action-wrap" v-if="rsvpEnabled">
      <div class="section-title">Will you be attending?</div>

      <div class="rsvp-action-bar">
        <button class="btn btn-primary w-100" @click="openEntryModal">
          {{ hasExisting ? 'Update RSVP' : 'Submit RSVP' }}
        </button>
      </div>

      <small class="text-muted d-block mt-2" v-if="(isLoggedIn || activeContactLocal) && Number(existing.status)>0">
        You previously responded:
        <strong>
          {{ Number(existing.status)===1 ? 'YES' :
             Number(existing.status)===2 ? 'NO' : 'MAY BE' }}
        </strong>
        <span v-if="rsvpFamilyEnabled">(Adults: {{existing.adult||0}}, Kids: {{existing.child||0}})</span>
      </small>
    </div>

    <!-- Entry modal -->
    <div
      v-if="ui.showEntryModal"
      class="modal-overlay entry-overlay"
      @click.self="ui.showEntryModal=false"
    >
      <div class="modal-card entry-modal" role="dialog" aria-modal="true" aria-labelledby="entryModalTitle">
        <div class="entry-handle" aria-hidden="true"></div>
        <div class="entry-header">
          <h5 id="entryModalTitle" class="mb-1">How would you like to RSVP?</h5>
          <p class="text-muted mb-3 small">Choose the fastest option. You can always create an account later.</p>
        </div>

        <div class="entry-options">
          <button type="button" class="entry-option" @click="handleEntryChoice('guest')">
            <div class="entry-icon guest"><i class="fas fa-user-check"></i></div>
            <div class="entry-copy">
              <div class="entry-title">Continue as Guest</div>
              <div class="entry-sub">No account needed. Share only what the host requires.</div>
            </div>
            <span class="entry-pill">Fastest</span>
          </button>

          <button type="button" class="entry-option" @click="handleEntryChoice('signin')">
            <div class="entry-icon signin"><i class="fas fa-lock"></i></div>
            <div class="entry-copy">
              <div class="entry-title">Sign in to Invi8</div>
              <div class="entry-sub">See friends, past events, photos, and your RSVPs.</div>
            </div>
          </button>
        </div>

        <button type="button" class="entry-cancel" @click="ui.showEntryModal=false">Not now</button>
      </div>
    </div>

    <!-- ===== Logged-in compact modal ===== -->
    <div v-if="ui.showLoggedModal" class="modal-mask" @click.self="ui.showLoggedModal=false">
      <div class="modal-card rsvp-modal">
        <div class="modal-header">
          <strong>Update RSVP?</strong>
          <button class="close-btn" @click="ui.showLoggedModal=false" aria-label="Close">&times;</button>
        </div>

        <div class="modal-body rsvp-body">
          <div class="rsvp-choice-row" v-if="rsvpEnabled">
            <button
              type="button"
              class="rsvp-chip yes"
              :class="{ active: choiceNum === 1 }"
              @click="setChoice('1')"
            >
              Yes
            </button>
            <button
              type="button"
              class="rsvp-chip no"
              :class="{ active: choiceNum === 2 }"
              @click="setChoice('2')"
            >
              No
            </button>
            <button
              type="button"
              class="rsvp-chip maybe"
              :class="{ active: choiceNum === 3 }"
              @click="setChoice('3')"
            >
              Maybe
            </button>
          </div>

          <div class="rsvp-grid" v-if="rsvpFamilyEnabled">
            <div class="rsvp-counter">
              <label for="logged-adults">Adults</label>
              <div class="stepper" :class="{ disabled: isChoiceNo }">
                <button
                  type="button"
                  class="step-btn"
                  :disabled="isChoiceNo || ui.counts.adults <= 0"
                  @click="changeLoggedCount('adults', -1)"
                >−</button>
                <div class="step-value" :aria-live="isChoiceNo ? 'off' : 'polite'">{{ ui.counts.adults }}</div>
                <button
                  type="button"
                  class="step-btn"
                  :disabled="isChoiceNo"
                  @click="changeLoggedCount('adults', 1)"
                >+</button>
              </div>
            </div>
            <div class="rsvp-counter">
              <label for="logged-children">Children</label>
              <div class="stepper" :class="{ disabled: isChoiceNo }">
                <button
                  type="button"
                  class="step-btn"
                  :disabled="isChoiceNo || ui.counts.kids <= 0"
                  @click="changeLoggedCount('kids', -1)"
                >−</button>
                <div class="step-value" :aria-live="isChoiceNo ? 'off' : 'polite'">{{ ui.counts.kids }}</div>
                <button
                  type="button"
                  class="step-btn"
                  :disabled="isChoiceNo"
                  @click="changeLoggedCount('kids', 1)"
                >+</button>
              </div>
            </div>
          </div>

          <p v-if="isChoiceNo && rsvpFamilyEnabled" class="rsvp-note mb-0">
            When choosing <strong>No</strong>, adults and children are set to 0.
          </p>

          <div class="form-group mb-0">
            <label class="small" for="logged-rsvp-notes">Notes (optional)</label>
            <textarea
              id="logged-rsvp-notes"
              class="form-control"
              rows="3"
              v-model.trim="ui.notes"
              placeholder="Add any notes for the host"
            ></textarea>
          </div>
        </div>

        <div class="modal-footer rsvp-actions">
          <button class="btn btn-light rsvp-btn" @click="ui.showLoggedModal=false">Cancel</button>
          <button class="btn btn-primary rsvp-btn primary" :disabled="savingLogged" @click="submitLoggedRsvp">
            <span v-if="!savingLogged">Update RSVP</span>
            <span v-else class="spinner-border spinner-border-sm">Saving</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ===== Public RSVP modal (first-time + update) ===== -->
    <div
      v-if="ui.showPublicModal || ui.showPublicUpdateModal"
      class="modal-overlay"
      @click.self="closePublicModal"
    >
      <div class="modal-card rsvp-modal" role="dialog" aria-modal="true" aria-labelledby="publicRsvpTitle">
        <div class="modal-header">
          <h5 id="publicRsvpTitle" class="mb-0">{{ publicModalTitle }}</h5>
          <button class="close-btn" @click="closePublicModal" aria-label="Close">&times;</button>
        </div>

        <div class="modal-body rsvp-body">
          <div class="rsvp-choice-row" v-if="rsvpEnabled">
            <button
              type="button"
              class="rsvp-chip yes"
              :class="{ active: choiceNum === 1 }"
              @click="setChoice('1')"
            >
              Yes
            </button>
            <button
              type="button"
              class="rsvp-chip no"
              :class="{ active: choiceNum === 2 }"
              @click="setChoice('2')"
            >
              No
            </button>
            <button
              type="button"
              class="rsvp-chip maybe"
              :class="{ active: choiceNum === 3 }"
              @click="setChoice('3')"
            >
              Maybe
            </button>
          </div>

          <div class="row g-2 mt-2">
            <div class="col-12 mb-3">
              <input type="text" class="form-control" placeholder="Name" v-model.trim="pubForm.name" />
            </div>

            <div class="col-12 mb-3">
              <input type="email" class="form-control" placeholder="Email Id" v-model.trim="pubForm.email" />
            </div>

            <!-- Custom searchable phone-code picker + phone field -->
            <div class="col-12 mb-3">
              <div class="phone-input">
                <button class="code-chip" @click="toggleCodeDropdown" type="button" :aria-expanded="pickerOpen ? 'true' : 'false'">
                  <span class="flag" v-if="selectedCode.flag">{{ selectedCode.flag }}</span>
                  <span class="dial">{{ pubForm.phone_code }}</span>
                  <i class="fas fa-chevron-down ml-1"></i>
                </button>
                <input
                  type="tel"
                  class="form-control flex-1 phone-box"
                  placeholder="Phone Number"
                  v-model.trim="pubForm.phone_local"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  :maxlength="MAX_PHONE_DIGITS"
                  @input="sanitizePhoneInput"
                  @keydown="onKeydownNumeric"
                  @paste="onPasteNumeric"
                  @drop.prevent
                />

                <!-- Dropdown -->
                <div v-if="pickerOpen" class="picker-popover" @keydown.esc="pickerOpen=false">
                  <div class="picker-search">
                    <i class="fas fa-search"></i>
                    <input
                      v-model.trim="pickerQuery"
                      type="text"
                      placeholder="Search country or code"
                      @keydown.stop
                      ref="pickerSearchEl"
                    />
                  </div>
                  <div class="picker-list" role="listbox">
                    <button
                      v-for="c in filteredCountries"
                      :key="c.code"
                      class="picker-row"
                      role="option"
                      @click="selectCode(c)"
                    >
                      <span class="flag">{{ c.flag }}</span>
                      <span class="country">{{ c.name }}</span>
                      <span class="dial">{{ c.dial }}</span>
                    </button>
                    <div v-if="filteredCountries.length===0" class="picker-empty">No matches</div>
                  </div>
                </div>
              </div>
              <small class="text-muted d-block mt-1">
              </small>
            </div>
          </div>

          <!-- Counts -->
          <div class="rsvp-grid mt-2" v-if="rsvpFamilyEnabled">
            <div class="rsvp-counter">
              <label for="public-adults">Adults</label>
              <div class="stepper" :class="{ disabled: isChoiceNo }">
                <button
                  type="button"
                  class="step-btn"
                  :disabled="isChoiceNo || pubForm.adults <= 0"
                  @click="changePublicCount('adults', -1)"
                >−</button>
                <div class="step-value" :aria-live="isChoiceNo ? 'off' : 'polite'">{{ pubForm.adults }}</div>
                <button
                  type="button"
                  class="step-btn"
                  :disabled="isChoiceNo"
                  @click="changePublicCount('adults', 1)"
                >+</button>
              </div>
            </div>
            <div class="rsvp-counter">
              <label for="public-children">Children</label>
              <div class="stepper" :class="{ disabled: isChoiceNo }">
                <button
                  type="button"
                  class="step-btn"
                  :disabled="isChoiceNo || pubForm.kids <= 0"
                  @click="changePublicCount('kids', -1)"
                >−</button>
                <div class="step-value" :aria-live="isChoiceNo ? 'off' : 'polite'">{{ pubForm.kids }}</div>
                <button
                  type="button"
                  class="step-btn"
                  :disabled="isChoiceNo"
                  @click="changePublicCount('kids', 1)"
                >+</button>
              </div>
            </div>
          </div>

          <p v-if="isChoiceNo && rsvpFamilyEnabled" class="rsvp-note mb-0">
            When choosing <strong>No</strong>, adults and children are set to 0.
          </p>

          <div class="form-group mb-0">
            <label class="small" for="public-rsvp-notes">Notes (optional)</label>
            <textarea
              id="public-rsvp-notes"
              class="form-control"
              rows="3"
              v-model.trim="pubForm.notes"
              placeholder="Add any notes for the host"
            ></textarea>
          </div>
        </div>

        <div class="modal-footer rsvp-actions">
          <button class="btn btn-light rsvp-btn" @click="closePublicModal">Cancel</button>
          <button class="btn btn-primary rsvp-btn primary" :disabled="savingPublic" @click="submitPublicRsvp">
            <span v-if="!savingPublic">{{ publicSubmitLabel }}</span>
            <span v-else class="spinner-border spinner-border-sm">Saving</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import Swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.min.css'

export default defineComponent({
  name: 'PublicRsvp',
  props: {
    eventId: { type: String, required: true },
    rawEventId: { type: String, default: '' },
    isLoggedIn: { type: Boolean, default: false },
    sessionUserId: { type: [String, Number], default: '' },
    sessionEmail: { type: String, default: '' },
    sessionPhone: { type: String, default: '' },
    initialContact: { type: String, default: '' },
    rsvpOption: { type: [String, Number], default: '1' },
    rsvpFamily: { type: [String, Number], default: '1' },
    existingRsvp: {
      type: Object,
      default: () => ({ status: 0, adult: 0, child: 0 })
    }
  },
  emits: ['rsvp-updated', 'contact-updated', 'need-profile', 'invitee-resolved'],
  setup(props, { emit }) {
    const router = useRouter()

    const toast = (icon, title) =>
      Swal.fire({ toast: true, position: 'top-end', icon, title, timer: 2200, showConfirmButton: false })

    const b64urlEncode = (str) => {
      try { return btoa(String(str)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '') } catch { return '' }
    }
    const isEmail = (s) => /\S+@\S+\.\S+/.test(String(s||'').trim())
    const preferEmail = (a,b) => (isEmail(a) ? a : (isEmail(b) ? b : ''))
    const normalizePhone10 = (val) => {
      const digits = String(val || '').replace(/\D/g, '')
      if (!digits) return ''
      return digits.length > 10 ? digits.slice(-10) : digits
    }
    const pickContact = (...vals) => {
      for (const v of vals) {
        const s = String(v || '').trim()
        if (s) return s
      }
      return ''
    }

    const normalizeFlag = (val) => {
      const s = String(val ?? '').trim().toLowerCase()
      if (s === '1' || s === 'yes' || s === 'true') return true
      return Number(s) === 1
    }

    const rsvpEnabled = computed(() => normalizeFlag(props.rsvpOption))
    const rsvpFamilyEnabled = computed(() => normalizeFlag(props.rsvpFamily))

    const existing = computed(() => ({
      status: Number(props.existingRsvp?.status || 0),
      adult: Number(props.existingRsvp?.adult || 0),
      child: Number(props.existingRsvp?.child || 0),
      note: props.existingRsvp?.note || props.existingRsvp?.notes || props.existingRsvp?.response_note || ''
    }))

    const activeContactLocal = ref(props.initialContact || props.sessionEmail || props.sessionPhone || '')
    watch(
      () => [props.initialContact, props.sessionEmail, props.sessionPhone],
      () => {
        if (!activeContactLocal.value) {
          activeContactLocal.value = props.initialContact || props.sessionEmail || props.sessionPhone || ''
        }
      }
    )

    const hasExisting = computed(() => {
      const r = existing.value
      return !!(r.status || r.adult || r.child)
    })

    const choiceNum = computed(() => Number(ui.value.choice || 1))
    const isChoiceNo = computed(() => choiceNum.value === 2)
    const publicModalTitle = computed(() =>
      ui.value.showPublicUpdateModal || hasExisting.value ? 'Update your RSVP' : 'Submit RSVP'
    )
    const publicSubmitLabel = computed(() =>
      ui.value.showPublicUpdateModal || hasExisting.value ? 'Update RSVP' : 'Submit RSVP'
    )
    const savingPublic = ref(false)
    const savingLogged = ref(false)

    const ui = ref({
      choice: '1',
      showEntryModal: false,
      showLoggedModal: false,
      showPublicModal: false,
      showPublicUpdateModal: false,
      counts: { adults: 0, kids: 0 },
      notes: ''
    })

    const pubForm = ref({
      name: '',
      email: '',
      phone_code: '+91',   // default India
      phone_local: '',
      adults: 0,
      kids: 0,
      notes: ''
    })

    /* ========== Custom code picker state ========== */

    const MAX_PHONE_DIGITS = 10

    function sanitizePhoneInput(e) {
      const cleaned = String(e.target.value || '').replace(/\D/g, '').slice(0, MAX_PHONE_DIGITS)
      e.target.value = cleaned
      pubForm.value.phone_local = cleaned
    }

    function onKeydownNumeric(e) {
      // allow control keys
      const ctrlKeys = [
        'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'Home', 'End'
      ]
      if (ctrlKeys.includes(e.key)) return

      // block non-digits
      if (!/^\d$/.test(e.key)) {
        e.preventDefault()
        return
      }

      // enforce max digits cap
      const val = String(pubForm.value.phone_local || '')
      if (val.length >= MAX_PHONE_DIGITS) e.preventDefault()
    }

    function onPasteNumeric(e) {
      e.preventDefault()
      const text = (e.clipboardData || window.clipboardData).getData('text') || ''
      const cleaned = text.replace(/\D/g, '').slice(0, MAX_PHONE_DIGITS)
      pubForm.value.phone_local = cleaned
    }


    const pickerOpen = ref(false)
    const pickerQuery = ref('')
    const pickerSearchEl = ref(null)

    const countries = [
      { code: 'IN', dial: '+91',  name: 'India',            flag: '🇮🇳' },
      { code: 'US', dial: '+1',   name: 'United States',     flag: '🇺🇸' },
      { code: 'GB', dial: '+44',  name: 'United Kingdom',    flag: '🇬🇧' },
      { code: 'AE', dial: '+971', name: 'United Arab Emirates', flag: '🇦🇪' },
      { code: 'AU', dial: '+61',  name: 'Australia',         flag: '🇦🇺' },
      { code: 'CA', dial: '+1',   name: 'Canada',            flag: '🇨🇦' },
      { code: 'DE', dial: '+49',  name: 'Germany',           flag: '🇩🇪' },
      { code: 'FR', dial: '+33',  name: 'France',            flag: '🇫🇷' },
      { code: 'JP', dial: '+81',  name: 'Japan',             flag: '🇯🇵' },
      { code: 'SG', dial: '+65',  name: 'Singapore',         flag: '🇸🇬' },
      { code: 'NZ', dial: '+64',  name: 'New Zealand',       flag: '🇳🇿' },
      { code: 'SA', dial: '+966', name: 'Saudi Arabia',      flag: '🇸🇦' },
      { code: 'ZA', dial: '+27',  name: 'South Africa',      flag: '🇿🇦' },
      { code: 'IT', dial: '+39',  name: 'Italy',             flag: '🇮🇹' },
      { code: 'ES', dial: '+34',  name: 'Spain',             flag: '🇪🇸' },
      // add more as needed
    ]

    const selectedCode = computed(() => countries.find(c => c.dial === pubForm.value.phone_code) || { flag:'', dial: pubForm.value.phone_code })

    const filteredCountries = computed(() => {
      const q = pickerQuery.value.toLowerCase()
      if (!q) return countries
      return countries.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dial.replace('+','').includes(q.replace('+',''))
      )
    })

    function toggleCodeDropdown() {
      pickerOpen.value = !pickerOpen.value
      if (pickerOpen.value) nextTick(() => pickerSearchEl.value && pickerSearchEl.value.focus())
    }
    function selectCode(c) {
      pubForm.value.phone_code = c.dial
      pickerOpen.value = false
    }
    function handleOutsideClick(e) {
      const target = e.target
      // Close if click is outside popover and chip
      if (!target.closest('.picker-popover') && !target.closest('.code-chip')) {
        pickerOpen.value = false
      }
    }
    onBeforeUnmount(() => window.removeEventListener('click', handleOutsideClick))
    nextTick(() => window.addEventListener('click', handleOutsideClick, { passive: true }))

    const updateBodyScrollLock = (locked) => {
      if (typeof document === 'undefined') return
      document.body.classList.toggle('modal-open', !!locked)
    }

    watch(
      () => [ui.value.showEntryModal, ui.value.showLoggedModal, ui.value.showPublicModal, ui.value.showPublicUpdateModal],
      (vals) => {
        const anyOpen = vals.some(Boolean)
        updateBodyScrollLock(anyOpen)
      },
      { immediate: true }
    )

    onBeforeUnmount(() => updateBodyScrollLock(false))

    /* ========== Helpers ========== */
    const openEntryModal = async () => {
      const defaultChoice = String(existing.value.status || '1')
      if (props.isLoggedIn) {
        // Logged-in users skip the entry chooser and go straight to the RSVP modal
        await openRsvp(defaultChoice)
        return
      }
      if (hasExisting.value) {
        // Guests who already responded should go straight to update
        await openRsvp(defaultChoice)
        return
      }
      ui.value.showEntryModal = true
    }

    const clampNonNegativeInt = (n) => (Number.isFinite(+n) ? Math.max(0, Math.floor(+n)) : 0)
    function setChoice(val) {
      ui.value.choice = val
      if (val === '2') {
        ui.value.counts.adults = 0
        ui.value.counts.kids = 0
        pubForm.value.adults = 0
        pubForm.value.kids = 0
      }
    }
    function clampGuestCounts() {
      if (!rsvpFamilyEnabled.value) { pubForm.value.adults = 0; pubForm.value.kids = 0; return }
      if (ui.value.choice === '2') { pubForm.value.adults = 0; pubForm.value.kids = 0; return }
      pubForm.value.adults = clampNonNegativeInt(pubForm.value.adults)
      pubForm.value.kids = clampNonNegativeInt(pubForm.value.kids)
    }
    function clampLoggedCounts() {
      if (!rsvpFamilyEnabled.value) { ui.value.counts.adults = 0; ui.value.counts.kids = 0; return }
      if (ui.value.choice === '2') { ui.value.counts.adults = 0; ui.value.counts.kids = 0; return }
      ui.value.counts.adults = clampNonNegativeInt(ui.value.counts.adults)
      ui.value.counts.kids = clampNonNegativeInt(ui.value.counts.kids)
    }

    function splitE164(e164) {
      const s = String(e164 || '').trim()
      if (!s.startsWith('+')) return { phone_code: '', phone: s.replace(/\D/g,'') }
      const m = s.match(/^\+(\d{1,4})(\d*)$/)
      if (!m) return { phone_code: '', phone: s.replace(/\D/g,'') }
      return { phone_code: `+${m[1]}`, phone: (m[2] || '').replace(/\D/g,'') }
    }

    // Seed counts + contact into forms
    function hydrateFromExisting(options = {}) {
      const { skipContactSeed = false } = options
      const rsvp = existing.value
      setChoice(String(rsvp.status || '1'))

      const adult = rsvpFamilyEnabled.value ? clampNonNegativeInt(rsvp.adult || 0) : 0
      const child = rsvpFamilyEnabled.value ? clampNonNegativeInt(rsvp.child || 0) : 0

      ui.value.counts.adults = adult
      ui.value.counts.kids = child
      pubForm.value.adults = adult
      pubForm.value.kids = child

      if (!skipContactSeed) {
        if (!pubForm.value.email && isEmail(activeContactLocal.value)) {
          pubForm.value.email = activeContactLocal.value
        }
        if (!pubForm.value.phone_local && !isEmail(activeContactLocal.value) && activeContactLocal.value) {
          const { phone_code, phone } = splitE164(activeContactLocal.value)
          if (phone_code) pubForm.value.phone_code = phone_code
          if (phone)      pubForm.value.phone_local = phone
        }
      }
      if (!pubForm.value.name) pubForm.value.name = ''
      const noteVal = rsvp.note || ''
      ui.value.notes = noteVal
      pubForm.value.notes = noteVal
    }

    function seedContactFromActive() {
      if (isEmail(activeContactLocal.value)) {
        pubForm.value.email = pubForm.value.email || activeContactLocal.value
      } else if (activeContactLocal.value) {
        const { phone_code, phone } = splitE164(activeContactLocal.value)
        if (!pubForm.value.phone_local && phone) pubForm.value.phone_local = phone
        if (!pubForm.value.phone_code && phone_code) pubForm.value.phone_code = phone_code
      }
    }

    // ---------- API: checkExistingInvitation ----------
    async function checkExistingInvitation({ eventId, email, name }) {
      try {
        const fd = new FormData()
        fd.append('eventid', eventId || '')
        // API expects contact in the "email" field even when it's a phone number
        fd.append('email', String(email || '').trim())
        fd.append('name', name || '')

        const res = await api.checkExistingInvitation(fd)
        const ok = (res?.data?.status === 'ok' || res?.data?.status === true)
        if (!ok) return { id:null, name:'', phone:'', phone_code:'', counts:null }

        const ud = res?.data?.userdetails || {}
        const cd0 = Array.isArray(res?.data?.counts_data) ? (res.data.counts_data[0] || null) : null
        console.log(cd0);
        const guestPhone = cd0?.phone || ''
        const guestEmail = cd0?.email || ''
        const guestPhoneCode =
          cd0?.codes ||
          cd0?.phone_code ||
          (guestPhone && guestPhone.startsWith('+') ? splitE164(guestPhone).phone_code : '+91')

        return {
          id: ud?.id ? String(ud.id) : null,
          main_id: ud?.touserid ? String(ud.touserid) : null,
          name: ud?.contact_name || ud?.name || '',
          phone: guestPhone || '',
          phone_code: guestPhone ? (guestPhoneCode || '') : '',
          counts: cd0 ? {
            id: cd0.id ? String(cd0.id) : null,
            email: guestEmail || '',
            phone: guestPhone || '',
            phone_code: guestPhoneCode || '+91',
            sent_status: (cd0.sent_status != null ? Number(cd0.sent_status) : null),
            status: (cd0.status != null ? String(cd0.status) : null),
            adult: (cd0.adult != null ? Number(cd0.adult) : null),
            child: (cd0.child != null ? Number(cd0.child) : null),
            notes: cd0.notes || "",
          } : null,
        }
      } catch (e) {
        console.error('checkExistingInvitation failed', e)
        return { id:null, name:'', phone:'', phone_code:'', counts:null }
      }
    }

    async function preloadInviteeProfileForGuestUpdate() {
      const rawContact = pickContact(pubForm.value.email, activeContactLocal.value)
      const contactToTry = isEmail(rawContact) ? rawContact : normalizePhone10(rawContact)

      const resolved = await checkExistingInvitation({
        eventId: props.eventId,
        email: contactToTry,
        name: pubForm.value.name || ''
      })

      if (resolved.name) pubForm.value.name = resolved.name
      if (!pubForm.value.email) {
        const guestEmail = resolved?.counts?.email || ''
        if (guestEmail) pubForm.value.email = guestEmail
        else if (isEmail(activeContactLocal.value)) pubForm.value.email = activeContactLocal.value
      }
      if (resolved.phone) {
        if (resolved.phone_code || resolved?.counts?.phone_code) {
          pubForm.value.phone_code = resolved.phone_code || resolved?.counts?.phone_code || pubForm.value.phone_code
        }
        if (resolved.phone)      pubForm.value.phone_local = String(resolved.phone)
        if (!pubForm.value.phone_code && !isEmail(activeContactLocal.value) && activeContactLocal.value) {
          const { phone_code, phone } = splitE164(activeContactLocal.value)
          if (phone && String(phone) === String(resolved.phone) && phone_code) {
            pubForm.value.phone_code = phone_code
          }
        }
      } else if (resolved.phone === '' && resolved.phone_code === '') {
        // Explicitly clear phone when API returns empty values
        pubForm.value.phone_local = ''
      }

      if (resolved.counts) {
        const s = resolved.counts.status ? String(resolved.counts.status) : ui.value.choice
        setChoice(s)
        if (s !== '2' && rsvpFamilyEnabled.value) {
          if (Number.isFinite(resolved.counts.adult)) pubForm.value.adults = resolved.counts.adult
          if (Number.isFinite(resolved.counts.child)) pubForm.value.kids   = resolved.counts.child
          ui.value.counts.adults = pubForm.value.adults
          ui.value.counts.kids   = pubForm.value.kids
        }
        const noteFromApi = resolved.counts.notes || resolved.counts.note || ''
        if (noteFromApi) {
          ui.value.notes = noteFromApi
          pubForm.value.notes = noteFromApi
        }
      }

      if (resolved.id) emit('invitee-resolved', resolved.id)
      return resolved
    }

    // ----- APIs -----
    async function updateRSVPLogin({ status, adult, child, contactHint, overrideUserid, notes }) {
      const fd = new FormData()
      fd.append('eventid', props.eventId)
      fd.append('userid', overrideUserid || props.sessionUserId || '')
      fd.append('status', String(status))
      fd.append('adult', String(status==='2'?0:adult))
      fd.append('child', String(status==='2'?0:child))
      fd.append('source', 'web')
      fd.append('notes', (notes || '').trim())
      const hint = contactHint || activeContactLocal.value || props.sessionEmail || props.sessionPhone || ''
      if (hint) { hint.includes('@') ? fd.append('email', hint) : fd.append('phone', hint) }
      const res = await api.updateRSVPLogin(fd)
      const ok = res?.data?.status === true || res?.data?.status === 'ok'
      if (!ok) throw new Error(res?.data?.msg || 'Could not update RSVP')
      return res
    }

    async function updateRSVPGuest({ status, adult, child, contact, requestType, notes }) {
      const fd = new FormData()
      fd.append('eventid', props.eventId)

      // Email & Phone parts
      const email = isEmail(pubForm.value.email) ? pubForm.value.email
                   : (isEmail(contact) ? contact : '')
      let phone_code = (pubForm.value.phone_code || '').trim()
      const phone_local = normalizePhone10(pubForm.value.phone_local)
      if (!phone_local) phone_code = ''

      fd.append('email', email || '')
      fd.append('phone_code', phone_code || '')
      fd.append('phone', phone_local || '')
      if (pubForm.value.name) fd.append('name', pubForm.value.name)

      fd.append('status', String(status))
      fd.append('adult', String(status === '2' ? 0 : adult))
      fd.append('child', String(status === '2' ? 0 : child))
      fd.append('source', 'web')
      fd.append('request_type', requestType)
      fd.append('notes', (notes || '').trim())

      // timezone meta
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
      const mins = -new Date().getTimezoneOffset()
      const sign = mins >= 0 ? '+' : '-'
      const h = String(Math.floor(Math.abs(mins) / 60)).padStart(2, '0')
      const m = String(Math.abs(mins) % 60).padStart(2, '0')
      const zone = `GMT${sign}${h}:${m}`
      fd.append('timezone', tz)
      fd.append('zone', zone)

      const res = await api.updateRSVPGuest(fd)
      const ok = res?.data?.status === true || res?.data?.status === 'ok'
      if (!ok) throw new Error(res?.data?.msg || 'Could not submit RSVP')
      return res
    }

    // -------- OPEN MODAL ----------
    async function openRsvp(choice) {
      if (!rsvpEnabled.value) {
        Swal.fire({ icon: 'info', title: 'RSVP is not available for this event.' })
        return
      }
      if (!props.eventId) {
        Swal.fire({ icon: 'error', title: 'Missing event id' })
        return
      }
      ui.value.choice = choice

      if (props.isLoggedIn) {
        hydrateFromExisting()
        if (!props.sessionEmail && !props.sessionPhone) emit('need-profile')
        ui.value.showLoggedModal = true
      } else {
        // Guest path
        if (hasExisting.value) {
          hydrateFromExisting({ skipContactSeed: true })
          // Ensure guest update doesn't inherit a host phone from earlier state
          pubForm.value.phone_local = ''
          pubForm.value.phone_code = ''
          const resolved = await preloadInviteeProfileForGuestUpdate()
          if (resolved && resolved.phone === '' && resolved.phone_code === '') {
            pubForm.value.phone_local = ''
          }
          ui.value.showPublicUpdateModal = true
        } else {
          seedContactFromActive()
          hydrateFromExisting()
          ui.value.showPublicModal = true
          await nextTick()
        }
      }
    }

    // ----- Submit (logged-in) -----
    async function submitLoggedRsvp() {
      if (savingLogged.value) return
      try {
        savingLogged.value = true
        const choice = ui.value.choice
        let a = rsvpFamilyEnabled.value ? clampNonNegativeInt(ui.value.counts.adults) : 0
        let k = rsvpFamilyEnabled.value ? clampNonNegativeInt(ui.value.counts.kids) : 0
        if (choice === '2') { a = 0; k = 0 }
        if (rsvpFamilyEnabled.value && choice !== '2' && (a + k < 1)) {
          Swal.fire({ icon: 'warning', title: 'Please add at least 1 Adult or 1 Kid.' }); return
        }
        if (!props.sessionEmail && !props.sessionPhone) emit('need-profile')

        const contactPref = preferEmail(props.sessionEmail, activeContactLocal.value)
        const rawForCheck = pickContact(props.sessionEmail, activeContactLocal.value, props.sessionPhone)
        const contactForCheck = isEmail(rawForCheck) ? rawForCheck : normalizePhone10(rawForCheck)
        const resolved = await checkExistingInvitation({
          eventId: props.eventId,
          email: contactForCheck,
          name: ''
        })
        if (resolved?.id) emit('invitee-resolved', resolved.id)

        const normalizedHint = contactPref
          ? contactPref
          : (isEmail(activeContactLocal.value) ? activeContactLocal.value : normalizePhone10(activeContactLocal.value))

        await updateRSVPLogin({
          status: choice, adult: a, child: k,
          notes: ui.value.notes,
          contactHint: normalizedHint,
          overrideUserid: resolved?.main_id || undefined
        })

        const tokenContact = contactPref || normalizedHint || ''
        if (tokenContact) {
          activeContactLocal.value = tokenContact
          emit('contact-updated', tokenContact)
          const tokenEnc = b64urlEncode(tokenContact)
          const eidEnc = props.rawEventId || b64urlEncode(props.eventId)
          router.replace({ path: `/i/${eidEnc}/${tokenEnc}` })
        }

        emit('rsvp-updated', { status: choice, adult: a, child: k, notes: ui.value.notes || '' })
        ui.value.showLoggedModal = false
        toast('success', 'Your RSVP has been updated.')
      } catch (e) {
        Swal.fire({ icon: 'error', title: e?.message || 'Could not submit RSVP' })
        console.error('submitLoggedRsvp failed', e)
      } finally {
        savingLogged.value = false
      }
    } 

    // ----- Submit (everyone through guest endpoint for this public flow) -----
    function closePublicModal() {
      ui.value.showPublicModal = false
      ui.value.showPublicUpdateModal = false
    }

    function changePublicCount(field, delta) {
      if (!rsvpFamilyEnabled.value || isChoiceNo.value) return
      const apply = (val) => Math.max(0, Number(val || 0) + delta)
      if (field === 'adults') pubForm.value.adults = apply(pubForm.value.adults)
      if (field === 'kids') pubForm.value.kids = apply(pubForm.value.kids)
    }

    function changeLoggedCount(field, delta) {
      if (!rsvpFamilyEnabled.value || isChoiceNo.value) return
      const apply = (val) => Math.max(0, Number(val || 0) + delta)
      if (field === 'adults') ui.value.counts.adults = apply(ui.value.counts.adults)
      if (field === 'kids') ui.value.counts.kids = apply(ui.value.counts.kids)
    }

    async function submitPublicRsvp() {
      if (savingPublic.value) return
      const f = pubForm.value
      const isUpdate = ui.value.showPublicUpdateModal || hasExisting.value
      const reqType = isUpdate ? 'update' : 'guest'

      const fallbackContact =
        activeContactLocal.value ||
        props.sessionEmail ||
        props.sessionPhone ||
        ''

      // Basic identity
      if (!isUpdate) {
        const hasAnyContact = (!!f.email || !!f.phone_local || !!fallbackContact)
        if (!f.name || !hasAnyContact) {
          Swal.fire({ icon: 'warning', title: 'Please enter your name and an Email or Phone.' })
          return
        }
        if (!f.email && !f.phone_local && fallbackContact) {
          if (isEmail(fallbackContact)) f.email = fallbackContact
          else {
            const { phone_code, phone } = splitE164(fallbackContact)
            if (phone_code) f.phone_code = phone_code
            if (phone)      f.phone_local = phone
          }
        }
      } else if (!f.email && !f.phone_local && fallbackContact) {
        if (isEmail(fallbackContact)) f.email = fallbackContact
        else {
          const { phone_code, phone } = splitE164(fallbackContact)
          if (phone_code) f.phone_code = phone_code
          if (phone)      f.phone_local = phone
        }
      }

      const choice = ui.value.choice
      let a = rsvpFamilyEnabled.value ? clampNonNegativeInt(f.adults) : 0
      let k = rsvpFamilyEnabled.value ? clampNonNegativeInt(f.kids) : 0
      if (choice === '2') { a = 0; k = 0 }
      if (rsvpFamilyEnabled.value && choice !== '2' && (a + k < 1)) {
        Swal.fire({ icon: 'warning', title: 'Please add at least 1 Adult or 1 Kid.' })
        return
      }

      try {
        savingPublic.value = true
        const picked =
          (f.email && f.email.trim()) ||
          normalizePhone10(f.phone_local) ||
          (isEmail(fallbackContact) ? String(fallbackContact || '').trim() : normalizePhone10(fallbackContact || ''))

        await updateRSVPGuest({
          status: choice,
          adult: a,
          child: k,
          contact: picked,
          requestType: reqType,
          notes: f.notes
        })

        let tokenContact = ''
        if (f.email && f.email.trim()) tokenContact = f.email.trim()
        else if (f.phone_local) tokenContact = normalizePhone10(f.phone_local)
        else if (fallbackContact) {
          tokenContact = isEmail(fallbackContact)
            ? String(fallbackContact || '').trim()
            : normalizePhone10(fallbackContact)
        }
        if (tokenContact) {
          activeContactLocal.value = tokenContact
          emit('contact-updated', tokenContact)
          const tokenEnc = b64urlEncode(tokenContact)
          const eidEnc = props.rawEventId || b64urlEncode(props.eventId)
          router.replace({ path: `/i/${eidEnc}/${tokenEnc}` })
        }

        emit('rsvp-updated', { status: choice, adult: a, child: k, notes: f.notes || '' })
        closePublicModal()
        toast('success', isUpdate ? 'Your RSVP has been updated.' : 'Thanks! Your RSVP has been submitted.')
      } catch (e) {
        Swal.fire({ icon: 'error', title: e?.message || 'Could not submit RSVP' })
        console.error('public rsvp failed', e)
      } finally {
        savingPublic.value = false
      }
    }

    async function handleEntryChoice(mode) {
      ui.value.showEntryModal = false
      const defaultChoice = String(existing.value.status || '1')

      if (mode === 'guest') {
        await openRsvp(defaultChoice)
        return
      }

      if (mode === 'signin') {
        if (props.isLoggedIn) {
          await openRsvp(defaultChoice)
        } else {
          const redirect = router.currentRoute?.value?.fullPath || ''
          const query = redirect ? { redirect } : {}
          router.push({ path: '/login', query })
        }
      }
    }

    return {
      existing,
      activeContactLocal,
      hasExisting,
      ui,
      choiceNum,
      isChoiceNo,
      publicModalTitle,
      publicSubmitLabel,
      pubForm,
      openEntryModal,
      openRsvp,
      handleEntryChoice,
      setChoice,
      clampGuestCounts,
      clampLoggedCounts,
      changePublicCount,
      changeLoggedCount,
      closePublicModal,
      savingPublic,
      savingLogged,

      // custom phone picker
      pickerOpen,
      pickerQuery,
      pickerSearchEl,
      filteredCountries,
      selectedCode,
      toggleCodeDropdown,
      selectCode,
      onKeydownNumeric,
      onPasteNumeric,
      sanitizePhoneInput,
      MAX_PHONE_DIGITS,
      rsvpEnabled,
      rsvpFamilyEnabled,
      
      submitLoggedRsvp,
      submitPublicRsvp
    }
  }
})
</script>

<style scoped>
.section-title{font-weight:600;margin-bottom:.5rem}
.text-small{font-size:.9rem}

.rsvp-action-wrap{position:relative;}
.rsvp-action-bar{margin-top:.5rem;}
.rsvp-action-bar .btn{font-weight:700;}
@media (max-width:768px){
  .rsvp-action-wrap{
    position:sticky;
    bottom:0;
    z-index:5;
    background:#fff;
    padding:.75rem .25rem;
    box-shadow:0 -10px 28px rgba(15,23,42,.08);
  }
}

.badge{display:inline-block;padding:.35em .6em;border-radius:6px}
.badge-success{background:#d4edda;color:#155724}
.badge-danger{background:#f8d7da;color:#721c24}
.badge-primary{background:#cfe2ff;color:#084298}
.badge-secondary{background:#ececec;color:#333}

.modal-overlay{
  position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:1060;
  display:flex;align-items:center;justify-content:center;padding:1rem;
  overflow-y:auto;-webkit-overflow-scrolling:touch;
}
.entry-overlay{padding:1.25rem;align-items:center;}
.entry-modal{background:#fdfcff;padding:1.1rem;max-width:520px;max-height:90vh;overflow:auto;}
.entry-handle{width:48px;height:5px;border-radius:999px;background:#e5e7eb;margin:0 auto .75rem;}
.entry-header h5{font-weight:700;}
.entry-options{display:flex;flex-direction:column;gap:.75rem;}
.entry-option{
  width:100%;border:1px solid #e5e7eb;background:#fff;border-radius:14px;padding:.85rem;
  display:flex;align-items:center;gap:.85rem;text-align:left;cursor:pointer;
  box-shadow:0 16px 32px rgba(71,85,105,.12);transition:transform .12s ease,box-shadow .12s ease;
}
.entry-option:hover{transform:translateY(-1px);box-shadow:0 18px 34px rgba(71,85,105,.16);}
.entry-icon{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:1.1rem;}
.entry-icon.guest{background:#e8f7f0;color:#0f9d58;}
.entry-icon.signin{background:#eef2ff;color:#4338ca;}
.entry-copy{flex:1;}
.entry-title{font-weight:700;color:#111827;}
.entry-sub{color:#6b7280;font-size:.92rem;}
.entry-pill{background:#eef2ff;color:#4338ca;font-weight:700;font-size:.85rem;padding:.25rem .65rem;border-radius:999px;}
.entry-cancel{
  margin-top:1rem;width:100%;border:0;background:#f8fafc;color:#111827;
  font-weight:700;border-radius:12px;padding:.75rem;
}
@media (max-width:768px){
  .entry-overlay{align-items:flex-end;}
  .entry-modal{border-radius:14px 14px 0 0;}
}
.rsvp-modal{border-radius:18px;max-width:720px;width:100%;box-shadow:0 18px 48px rgba(0,0,0,.18);max-height:90vh;display:flex;flex-direction:column;}
.rsvp-body{display:flex;flex-direction:column;gap:1rem;padding:1.1rem;overflow:auto;min-height:0;}
.rsvp-choice-row{display:grid;grid-template-columns:repeat(auto-fit,minmax(96px,1fr));gap:.55rem;}
.rsvp-chip{
  border:1px solid #e5e7eb;
  background:#ffffff;
  color:#111827;
  border-radius:999px;
  padding:.55rem .6rem;
  font-weight:700;
  font-size:.92rem;
  text-transform:capitalize;
  width:100%;
  min-width:0;
  transition:all .15s ease;
  box-shadow:0 6px 16px rgba(15,23,42,.06);
}
.rsvp-chip.active{color:#0f172a;border-color:transparent;box-shadow:0 10px 18px rgba(251,114,16,.22);}
.rsvp-chip.yes.active{background:#FB7210;color:#fff;}
.rsvp-chip.no.active{background:#e5e7eb;color:#374151;}
.rsvp-chip.maybe.active{background:#e7eef9;color:#1f2a44;}
.rsvp-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:.75rem;}
.rsvp-counter{background:#f7f9fb;border:1px solid #e2e8f0;border-radius:16px;padding:.9rem;}
.rsvp-counter label{display:block;margin-bottom:.35rem;font-weight:700;color:#0f172a;}
.stepper{display:grid;grid-template-columns:repeat(3,1fr);border:1px solid #d8e2ec;border-radius:12px;overflow:hidden;background:#f4f8fb;}
.stepper.disabled{opacity:.65;}
.step-btn{border:0;background:transparent;color:#1f2937;font-weight:700;padding:.55rem 0;transition:background .12s ease;}
.step-btn:first-child{border-right:1px solid #e5e7eb;}
.step-btn:last-child{border-left:1px solid #e5e7eb;}
.step-btn:active{background:#e6edf5;}
.step-value{display:flex;align-items:center;justify-content:center;font-weight:700;color:#111827;padding:.55rem 0;}
.rsvp-note{font-size:.9rem;color:#6b7280;}
.rsvp-actions{gap:.65rem;flex-wrap:wrap;display:flex;justify-content:flex-end;padding:12px 16px;}
.rsvp-btn{min-width:120px;border-radius:12px;font-weight:700;}
.rsvp-btn.primary{box-shadow:0 12px 24px rgba(37,99,235,.25);}

@media (max-width:576px){
  .rsvp-choice-row{grid-template-columns:repeat(auto-fit,minmax(80px,1fr));gap:.45rem;}
  .rsvp-chip{padding:.5rem .5rem;font-size:.88rem;}
}

.modal-mask{position:fixed;z-index:1050;inset:0;background:rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;padding:1rem;overflow-y:auto;-webkit-overflow-scrolling:touch;}
.modal-card{background:#fff;border-radius:12px;max-width:560px;width:100%;box-shadow:0 10px 30px rgba(0,0,0,.25);overflow:hidden;display:flex;flex-direction:column;max-height:90vh;}
.modal-card.lg{max-width:720px}
.modal-header{display:flex;align-items:center;justify-content:space-between;padding:.85rem 1rem;border-bottom:1px solid #f1f3f5}
.modal-body{padding:1rem;overflow:auto;min-height:0}
.modal-footer{padding:12px 16px;border-top:1px solid #f1f3f5;display:flex;justify-content:flex-end;gap:8px}
.close{border:none;background:transparent;font-size:20px;line-height:1}
.close-btn{background:transparent;border:0;font-size:1.3rem;color:#6c757d;cursor:pointer;}
.close-btn:hover{color:#000;}

.modal-open{
  overflow:hidden;
  position:fixed;
  width:100%;
}

/* Phone input group */
.phone-input{
  display:flex;
  align-items:center;
  gap:.6rem;
  position:relative;
  flex-wrap:wrap;
}
.code-chip{
  display:inline-flex;
  align-items:center;
  gap:.35rem;
  height:44px;
  padding:0 .8rem;
  border:1px solid #d8e2ec;
  border-radius:12px;
  background:#fff;
  cursor:pointer;
  box-shadow:0 8px 18px rgba(15,23,42,.08);
  transition:box-shadow .12s ease,transform .12s ease;
}
.code-chip:hover{box-shadow:0 10px 20px rgba(15,23,42,.12);transform:translateY(-1px);}
.code-chip .dial{font-weight:700;color:#111827;}
.code-chip i{color:#6b7280;font-size:.85rem;}
.form-control.flex-1{flex:1;min-width:0;}
.phone-box{
  height:44px;
  border-radius:12px;
  border:1px solid #d8e2ec;
  padding-left:.9rem;
  box-shadow:inset 0 1px 2px rgba(0,0,0,.04);
}
.phone-box:focus{
  border-color:#0d6efd;
  box-shadow:0 0 0 3px rgba(13,110,253,.12);
}

/* Popover */
.picker-popover{
  position:absolute;left:0;top:48px;width:240px;max-height:320px;background:#fff;border:1px solid rgba(0,0,0,.08);
  border-radius:.5rem;box-shadow:0 8px 24px rgba(0,0,0,.15);z-index:1060;overflow:hidden;display:flex;flex-direction:column;
}
.picker-search{display:flex;align-items:center;gap:.5rem;padding:.5rem .6rem;border-bottom:1px solid rgba(0,0,0,.06)}
.picker-search input{width:100%;border:1px solid #e5e7eb;border-radius:.35rem;padding:.4rem .6rem}
.picker-list{overflow:auto;max-height:260px}
.picker-row{
  width:100%;display:flex;align-items:center;gap:.6rem;justify-content:space-between;
  padding:.45rem .75rem;border:0;background:#fff;cursor:pointer;text-align:left;
}
.picker-row:hover{background:#f8fafc}
.picker-row .country{flex:1}
.picker-empty{padding:.75rem;color:#6c757d;text-align:center}

.flag{width:22px;display:inline-block}
</style>
